import { API_ENDPOINTS } from "configs/apiConstants";
import { REACT_APP_BACKEND_PROTECTED_URL } from "configs/config";
import { IUserBasic } from "types/authz/IUserBasic";

import { AuthenticationError, BackendError } from "../types/errors/ApiErrors";
import {
  ISyncStripeRequest,
  ISyncStripeResponse,
  IUpdateUserProfileRequest,
  IUpdateUserProfileResponse,
  IUserProfileResponse,
  IUserQuotaResponse,
} from "../types/subscription/IUserApi";
import { IUserProfile } from "../types/subscription/IUserProfile";

import { BaseApiService } from "./BaseApiService";

/**
 * Service responsible for user-related API operations
 * Follows Single Responsibility Principle
 */
export class UserService extends BaseApiService {
  /**
   * Verifies the user's token with the backend. This is intended to be called
   * once per session to ensure the user is synced in all backend systems (e.g., Firebase, Stripe).
   * @param authToken The user's access token from Auth0.
   * @returns The latest user profile from the backend.
   */
  static async verifyUserAsync(authToken: string): Promise<{ user: IUserProfile }> {
    // This POST request triggers the getOrCreateUserWithAuth0Data logic on the backend.
    return this.postAsync<{ user: IUserProfile }>(API_ENDPOINTS.USER.AUTH_TOKENS, authToken);
  }

  static async getUserProfile(authToken: string): Promise<IUserProfileResponse> {
    return this.getAsync<IUserProfileResponse>(API_ENDPOINTS.USER.PROFILE, authToken);
  }

  static async updateUserProfile(
    request: IUpdateUserProfileRequest,
    authToken: string
  ): Promise<IUpdateUserProfileResponse> {
    return this.putAsync<IUpdateUserProfileResponse>(API_ENDPOINTS.USER.PROFILE, authToken, request);
  }

  static async getUserQuota(authToken: string): Promise<IUserQuotaResponse> {
    return this.getAsync<IUserQuotaResponse>(API_ENDPOINTS.USER.QUOTA, authToken);
  }

  static async syncStripeData(request: ISyncStripeRequest = {}, authToken: string): Promise<ISyncStripeResponse> {
    return this.postAsync<ISyncStripeResponse>(API_ENDPOINTS.USER.SYNC_STRIPE, authToken, request);
  }

  static async requestPasswordChange(authToken: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.postAsync<{ success: boolean; message: string }>(API_ENDPOINTS.USER.PASSWORD_CHANGE, authToken);
  }

  static async uploadAvatar(
    avatarFile: File,
    authToken: string
  ): Promise<{
    avatarGsUrl: string;
  }> {
    const formData = new FormData();
    formData.append("avatar", avatarFile);

    try {
      const response = await fetch(`${REACT_APP_BACKEND_PROTECTED_URL}${API_ENDPOINTS.PROFILE.AVATAR}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          throw new AuthenticationError({
            message: `Authentication failed: ${response.statusText}`,
            statusCode: response.status,
          });
        }

        const errorData = await response.json().catch(() => ({}));
        throw new BackendError({
          message: errorData.error || `API request failed: ${response.statusText}`,
          statusCode: response.status,
        });
      }

      return response.json();
    } catch (error) {
      // If it's already our custom error, rethrow it
      if (error instanceof AuthenticationError || error instanceof BackendError) {
        throw error;
      }

      // Network errors or other unexpected errors
      throw new BackendError({
        message: error instanceof Error ? error.message : "Unknown error occurred during avatar upload",
      });
    }
  }

  static async getStoragePublicUrl(resourceUrl: string, authToken: string): Promise<{ resourceUrl: string }> {
    return this.postAsync<{ resourceUrl: string }>(API_ENDPOINTS.STORAGE.PUBLIC_URL, authToken, { resourceUrl });
  }

  /**
   * Resolve a list of Auth0 user IDs to basic info (email/name).
   */
  static async resolveBasicUsers(params: { userIds: string[]; authToken: string }): Promise<IUserBasic[]> {
    const { userIds, authToken } = params;

    try {
      const res = await fetch(`${REACT_APP_BACKEND_PROTECTED_URL}/muggle-test/users/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ userIds }),
      });

      if (!res.ok) {
        // Handle authentication errors
        if (res.status === 401 || res.status === 403) {
          throw new AuthenticationError({
            message: `Authentication failed: ${res.statusText}`,
            statusCode: res.status,
          });
        }

        const err = await res.json().catch(() => ({}));
        throw new BackendError({
          message: err.error || "Failed to resolve users",
          statusCode: res.status,
        });
      }

      return res.json();
    } catch (error) {
      // If it's already our custom error, rethrow it
      if (error instanceof AuthenticationError || error instanceof BackendError) {
        throw error;
      }

      // Network errors or other unexpected errors
      throw new BackendError({
        message: error instanceof Error ? error.message : "Unknown error occurred while resolving users",
      });
    }
  }
}
